import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import RFQStatusBadge from './RFQStatusBadge';

const RFQDetailsModal = ({ rfq, isOpen, onClose, onDownloadQuote, onAcceptQuote, onRequestModification }) => {
  const [activeTab, setActiveTab] = useState('details');

  if (!isOpen || !rfq) return null;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const tabs = [
    { id: 'details', label: 'RFQ Details', icon: 'FileText' },
    { id: 'requirements', label: 'Requirements', icon: 'List' },
    { id: 'quote', label: 'Quote Response', icon: 'DollarSign' },
    { id: 'communication', label: 'Messages', icon: 'MessageSquare' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">{rfq?.rfqNumber}</h2>
              <p className="text-sm text-muted-foreground">{rfq?.serviceCategory}</p>
            </div>
            <RFQStatusBadge status={rfq?.status} />
          </div>
          <Button variant="ghost" onClick={onClose} iconName="X" />
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-smooth ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">RFQ Number</label>
                      <p className="text-sm text-foreground">{rfq?.rfqNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Service Category</label>
                      <p className="text-sm text-foreground">{rfq?.serviceCategory}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Submission Date</label>
                      <p className="text-sm text-foreground">{formatDate(rfq?.submissionDate)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Project Timeline</label>
                      <p className="text-sm text-foreground">{rfq?.projectTimeline || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Assignment Details</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Sales Representative</label>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-primary-foreground">
                            {rfq?.salesRep?.split(' ')?.map(n => n?.[0])?.join('')}
                          </span>
                        </div>
                        <span className="text-sm text-foreground">{rfq?.salesRep}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Estimated Response</label>
                      <p className="text-sm text-foreground">{formatDate(rfq?.estimatedResponse)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Priority Level</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rfq?.priority === 'high' ? 'bg-red-100 text-red-800' :
                        rfq?.priority === 'medium'? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {rfq?.priority || 'Normal'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'requirements' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Project Requirements</h3>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {rfq?.requirements || `We require a comprehensive ICT infrastructure assessment and upgrade plan for our organization. The scope includes:\n\n• Network infrastructure evaluation\n• Server and storage assessment\n• Security audit and recommendations\n• Cloud migration strategy\n• Staff training requirements\n\nThe project should be completed within 3 months with detailed documentation and implementation roadmap.`}
                  </p>
                </div>
              </div>
              
              {rfq?.attachments && rfq?.attachments?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Attachments</h3>
                  <div className="space-y-2">
                    {rfq?.attachments?.map((attachment, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground flex-1">{attachment?.name}</span>
                        <span className="text-xs text-muted-foreground">{attachment?.size}</span>
                        <Button variant="ghost" size="sm" iconName="Download">
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'quote' && (
            <div className="space-y-6">
              {rfq?.status === 'quoted' ? (
                <>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Quote Response</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="CheckCircle" size={16} className="text-green-600" />
                        <span className="text-sm font-medium text-green-800">Quote Ready</span>
                      </div>
                      <p className="text-sm text-green-700">
                        Your quote has been prepared and is ready for review. Please download the detailed proposal below.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">Quote Summary</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Base Cost:</span>
                          <span className="text-sm text-foreground">{formatCurrency(rfq?.quotedAmount * 0.8)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Additional Services:</span>
                          <span className="text-sm text-foreground">{formatCurrency(rfq?.quotedAmount * 0.15)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">VAT (18%):</span>
                          <span className="text-sm text-foreground">{formatCurrency(rfq?.quotedAmount * 0.05)}</span>
                        </div>
                        <div className="border-t border-border pt-2">
                          <div className="flex justify-between">
                            <span className="font-medium text-foreground">Total Amount:</span>
                            <span className="font-bold text-foreground">{formatCurrency(rfq?.quotedAmount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">Timeline</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Project Duration:</span>
                          <span className="text-sm text-foreground">12 weeks</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Start Date:</span>
                          <span className="text-sm text-foreground">Within 2 weeks</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Quote Valid Until:</span>
                          <span className="text-sm text-foreground">
                            {formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Icon name="Clock" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">Quote Pending</h3>
                  <p className="text-muted-foreground">
                    Our team is working on your quote. You'll be notified once it's ready.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'communication' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">Communication History</h3>
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {rfq?.messages?.map((message, index) => (
                  <div key={index} className={`flex ${message?.sender === 'You' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message?.sender === 'You' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
                    }`}>
                      <p className="text-sm">{message?.content}</p>
                      <p className={`text-xs mt-1 ${
                        message?.sender === 'You' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        {message?.sender} • {formatDate(message?.timestamp)}
                      </p>
                    </div>
                  </div>
                )) || (
                  <div className="text-center py-8">
                    <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No messages yet</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Last updated: {formatDate(rfq?.lastUpdated || rfq?.submissionDate)}
            </span>
          </div>
          <div className="flex items-center space-x-3">
            {rfq?.status === 'quoted' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => onDownloadQuote(rfq)}
                  iconName="Download"
                  iconPosition="left"
                >
                  Download Quote
                </Button>
                <Button
                  variant="default"
                  onClick={() => onAcceptQuote(rfq)}
                  iconName="Check"
                  iconPosition="left"
                >
                  Accept Quote
                </Button>
              </>
            )}
            {(rfq?.status === 'quoted' || rfq?.status === 'under_review') && (
              <Button
                variant="ghost"
                onClick={() => onRequestModification(rfq)}
                iconName="Edit"
                iconPosition="left"
              >
                Request Changes
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFQDetailsModal;